import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Mail, Phone, Building2, MapPin, RefreshCw, Inbox } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const STATUS_META = {
  new: { label: "New", cls: "bg-secondary/15 text-secondary border-secondary/30" },
  contacted: { label: "Contacted", cls: "bg-blue-500/10 text-blue-600 border-blue-500/30" },
  resolved: { label: "Resolved", cls: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" },
};

const FILTERS = [
  { key: "all", label: "All" }, { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" }, { key: "resolved", label: "Resolved" },
];

const StatCard = ({ label, value, testId }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testId}>
    <div className="font-heading text-3xl font-semibold text-primary">{value}</div>
    <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{label}</div>
  </div>
);

const QuoteCard = ({ q, onStatus, onDelete }) => {
  const meta = STATUS_META[q.status] || STATUS_META.new;
  const date = q.created_at ? new Date(q.created_at).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }) : "";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 md:p-6" data-testid={`admin-quote-${q.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="font-heading text-lg font-semibold text-primary">{q.full_name}</h3>
            <span className={cn("text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full border", meta.cls)}>{meta.label}</span>
          </div>
          {q.company_name && <div className="text-sm text-muted-foreground mt-0.5">{q.company_name}</div>}
        </div>
        <div className="text-xs text-muted-foreground">{date}</div>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        <a href={`mailto:${q.email}`} className="flex items-center gap-2 text-slate-700 hover:text-secondary"><Mail className="w-4 h-4 text-secondary" /> {q.email}</a>
        <a href={`tel:${q.phone}`} className="flex items-center gap-2 text-slate-700 hover:text-secondary"><Phone className="w-4 h-4 text-secondary" /> {q.phone}</a>
        <div className="flex items-center gap-2 text-slate-700"><Building2 className="w-4 h-4 text-secondary" /> {q.service_required}</div>
        {q.municipality && <div className="flex items-center gap-2 text-slate-700"><MapPin className="w-4 h-4 text-secondary" /> {q.municipality}</div>}
      </div>

      <div className="mt-3 flex flex-wrap gap-2 text-xs">
        {q.property_type && <span className="px-2.5 py-1 rounded-md bg-muted text-slate-600">{q.property_type}</span>}
        {q.council_account_number && <span className="px-2.5 py-1 rounded-md bg-muted text-slate-600">Acc: {q.council_account_number}</span>}
      </div>

      <p className="mt-4 text-sm text-slate-600 leading-relaxed bg-muted/50 rounded-lg p-3 whitespace-pre-wrap">{q.message}</p>

      <div className="mt-4 flex items-center justify-between gap-3">
        <div className="w-44">
          <Select value={q.status} onValueChange={(v) => onStatus(q.id, v)}>
            <SelectTrigger data-testid={`status-select-${q.id}`} className="h-10 bg-slate-50"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <button onClick={() => onDelete(q.id)} data-testid={`delete-${q.id}`}
          className="inline-flex items-center gap-1.5 text-sm text-red-500 hover:text-red-600 font-medium">
          <Trash2 className="w-4 h-4" /> Delete
        </button>
      </div>
    </div>
  );
};

export default function Admin() {
  const { user, logout } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, resolved: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, s] = await Promise.all([api.get("/admin/quotes"), api.get("/admin/stats")]);
      setQuotes(q.data);
      setStats(s.data);
    } catch {
      toast.error("Failed to load enquiries");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/quotes/${id}`, { status });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      const s = await api.get("/admin/stats");
      setStats(s.data);
      toast.success("Status updated");
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    try {
      await api.delete(`/admin/quotes/${id}`);
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      const s = await api.get("/admin/stats");
      setStats(s.data);
      toast.success("Enquiry deleted");
    } catch { toast.error("Delete failed"); }
  };

  const filtered = filter === "all" ? quotes : quotes.filter((q) => q.status === filter);

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/"><img src="/logo-color.png" alt="SML Municipal Consulting" className="h-9 w-auto" /></Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-muted-foreground">{user?.email}</span>
            <button onClick={logout} data-testid="logout-btn"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-secondary transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-semibold text-primary">Enquiries Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage every Request for Quotation in one place.</p>
          </div>
          <button onClick={load} data-testid="refresh-btn" className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-secondary">
            <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} /> Refresh
          </button>
        </div>

        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Total" value={stats.total} testId="stat-total" />
          <StatCard label="New" value={stats.new} testId="stat-new" />
          <StatCard label="Contacted" value={stats.contacted} testId="stat-contacted" />
          <StatCard label="Resolved" value={stats.resolved} testId="stat-resolved" />
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}
              className={cn("px-4 py-2 rounded-full text-sm font-medium border transition-colors",
                filter === f.key ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400")}>
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-4">
          {loading ? (
            <div className="text-center py-20 text-muted-foreground">Loading enquiries…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground flex flex-col items-center gap-3" data-testid="empty-state">
              <Inbox className="w-10 h-10 text-slate-300" />
              No enquiries {filter !== "all" ? `with status "${filter}"` : "yet"}.
            </div>
          ) : (
            filtered.map((q) => <QuoteCard key={q.id} q={q} onStatus={updateStatus} onDelete={remove} />)
          )}
        </div>
      </main>
    </div>
  );
}
